import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useNavigate, Navigate } from "react-router-dom";
import { useEffect, lazy, Suspense } from "react";
import { Layout } from "./components/Layout";
import { ScrollToTop } from "./components/ScrollToTop";
import { AuthProvider } from "./hooks/useAuth";
import { CartProvider } from "./contexts/CartContext";
import { ReferralProvider } from "./contexts/ReferralContext";
// Eager: home page (LCP route) only
import Index from "./pages/Index";
import { LEGACY_REDIRECTS } from "./lib/legacyRedirects";

const Toaster = lazy(() => import("@/components/ui/toaster").then((m) => ({ default: m.Toaster })));
const Sonner = lazy(() => import("@/components/ui/sonner").then((m) => ({ default: m.Toaster })));

const ProtectedRoute = lazy(() =>
  import("./components/ProtectedRoute").then((m) => ({ default: m.ProtectedRoute }))
);

// Lazy-loaded routes — code-split to keep the home page bundle small
const About = lazy(() => import("./pages/About"));
const Contact = lazy(() => import("./pages/Contact"));
const Auth = lazy(() => import("./pages/Auth"));
const AdminSetup = lazy(() => import("./pages/AdminSetup"));
const NotFound = lazy(() => import("./pages/NotFound"));
const AdminDashboard = lazy(() =>
  import("./components/AdminDashboard").then((m) => ({ default: m.AdminDashboard }))
);
const AdminAnalytics = lazy(() => import("./pages/AdminAnalytics"));
const BrainCharge = lazy(() => import("./pages/BrainCharge"));
const PaymentSuccess = lazy(() => import("./pages/PaymentSuccess"));
const PaymentCanceled = lazy(() => import("./pages/PaymentCanceled"));
const Checkout = lazy(() => import("./pages/Checkout"));
const Privacy = lazy(() => import("./pages/Privacy"));
const Terms = lazy(() => import("./pages/Terms"));
const ShippingReturns = lazy(() => import("./pages/ShippingReturns"));
const RefundPolicy = lazy(() => import("./pages/RefundPolicy"));
const VerifyReview = lazy(() => import("./pages/VerifyReview"));
const AdminSecurity = lazy(() => import("./pages/AdminSecurity"));
const AdminAdRoi = lazy(() => import("./pages/AdminAdRoi"));
const ComingSoon = lazy(() => import("./pages/ComingSoon"));
const Impact = lazy(() => import("./pages/Impact"));
const Rewards = lazy(() => import("./pages/Rewards"));
const AdminReviews = lazy(() => import("./pages/AdminReviews"));
const Share = lazy(() => import("./pages/Share"));
const EmailUnsubscribe = lazy(() => import("./pages/EmailUnsubscribe"));
const LockIn = lazy(() => import("./pages/LockIn"));
const ContentStudio = lazy(() => import("./pages/ContentStudio"));
const AdminProductPhotos = lazy(() => import("./pages/AdminProductPhotos"));

const queryClient = new QueryClient();

const PageLoading = () => (
  <div className="flex min-h-[55vh] items-center justify-center bg-background px-6 text-center text-foreground" role="status">
    <div>
      <p className="font-display text-3xl text-primary">Proton Health Labs</p>
      <p className="mt-2 text-sm text-muted-foreground">Loading this chamber…</p>
    </div>
  </div>
);

const KeyboardShortcuts = () => {
  const navigate = useNavigate();

  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      // Ctrl+Shift+A for admin login
      if (e.ctrlKey && e.shiftKey && e.key === 'A') {
        e.preventDefault();
        navigate('/auth');
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [navigate]);

  return null;
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <AuthProvider>
      <CartProvider>
        <ReferralProvider>
          <TooltipProvider>
            <Suspense fallback={null}>
              <Toaster />
              <Sonner />
            </Suspense>
            <BrowserRouter>
              <KeyboardShortcuts />
              <ScrollToTop />
              <Suspense fallback={<PageLoading />}>
              <Routes>
                <Route path="/auth" element={<Auth />} />
                <Route path="/admin-setup" element={<AdminSetup />} />
                
                {/* Main Website Routes */}
                <Route path="/" element={<Layout />}>
                  <Route index element={<Index />} />
                  <Route path="index" element={<Navigate to="/" replace />} />
                  <Route path="about" element={<About />} />
                  <Route path="shop" element={<Navigate to="/products/brain-charge" replace />} />
                  <Route path="contact" element={<Contact />} />
                  <Route path="share" element={<Share />} />
                  <Route path="lock-in" element={<LockIn />} />
                  <Route path="content-studio" element={<ContentStudio />} />

                  {/* Retired public content routes resolve to the consolidated Brain Charge page. */}
                  <Route path="blog" element={<Navigate to="/about" replace />} />
                  <Route path="blog/:slug" element={<Navigate to="/products/brain-charge" replace />} />
                  <Route path="science" element={<Navigate to="/products/brain-charge#science" replace />} />
                  <Route path="science/brain-charge" element={<Navigate to="/products/brain-charge#science" replace />} />
                  <Route path="ingredients" element={<Navigate to="/products/brain-charge#ingredients" replace />} />
                  <Route path="ingredients/:slug" element={<Navigate to="/products/brain-charge#ingredients" replace />} />
                  <Route path="how-we-build" element={<Navigate to="/products/brain-charge#science" replace />} />
                  <Route path="impact" element={<Impact />} />
                  <Route path="rewards" element={<Rewards />} />
                  <Route path="app" element={<ComingSoon />} />
                  
                  {/* Product Pages */}
                  <Route path="products" element={<Navigate to="/products/brain-charge" replace />} />
                  <Route path="products/brain-charge" element={<BrainCharge />} />

                  {/* Legacy product URL redirects → Brain Charge (preserve old links / SEO) */}
                  {LEGACY_REDIRECTS.map((r) => (
                    <Route
                      key={r.from}
                      path={r.from.replace(/^\//, "")}
                      element={<Navigate to={r.to} replace />}
                    />
                  ))}
                  
                  {/* Legal & Policy Pages */}
                  <Route path="privacy" element={<Privacy />} />
                  <Route path="terms" element={<Terms />} />
                  <Route path="shipping-returns" element={<ShippingReturns />} />
                  <Route path="refund-policy" element={<RefundPolicy />} />
                  
                  {/* Utility Pages */}
                  <Route path="unsubscribe" element={<EmailUnsubscribe />} />
                  <Route path="email-unsubscribe" element={<EmailUnsubscribe />} />
                  <Route path="verify-review" element={<VerifyReview />} />
                  <Route path="payment-success" element={<PaymentSuccess />} />
                  <Route path="payment-canceled" element={<PaymentCanceled />} />
                  <Route path="checkout" element={<Checkout />} />
                  
                  {/* Admin Routes */}
                  <Route
                    path="admin" 
                    element={
                      <ProtectedRoute requireAdmin>
                        <AdminDashboard />
                      </ProtectedRoute>
                    } 
                  />
                  <Route
                    path="admin/security" 
                    element={
                      <ProtectedRoute requireAdmin>
                        <AdminSecurity />
                      </ProtectedRoute>
                    } 
                  />
                  <Route
                    path="admin/reviews"
                    element={
                      <ProtectedRoute requireAdmin>
                        <AdminReviews />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="admin/redirects"
                    element={<Navigate to="/products/brain-charge" replace />}
                  />
                  <Route
                    path="admin/ad-roi"
                    element={
                      <ProtectedRoute requireAdmin>
                        <AdminAdRoi />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="admin/product-photos"
                    element={
                      <ProtectedRoute requireAdmin>
                        <AdminProductPhotos />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="admin/analytics"
                    element={
                      <ProtectedRoute requireAdmin>
                        <AdminAnalytics />
                      </ProtectedRoute>
                    }
                  />
                  
                  {/* Catch-all 404 */}
                  <Route path="*" element={<NotFound />} />
                </Route>
              </Routes>
              </Suspense>
            </BrowserRouter>
          </TooltipProvider>
        </ReferralProvider>
      </CartProvider>
    </AuthProvider>
  </QueryClientProvider>
);

export default App;